import {
	IconCheck,
	IconClock,
	IconLock,
	IconMinus,
	IconShieldLock,
	IconX,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { cn } from "@/lib/utils";
import { MARKETING_SECTION_ANIM_BASE } from "@/utils/animation";
import { seo } from "@/utils/seo";

const faqs = [
	{
		question: "Is Guerrilla Mail free?",
		answer:
			"Yes, Guerrilla Mail is completely free with no registration, no paid tiers, and no feature caps. It has been free since 2006. ineedatest.email is also completely free with no paid tiers.",
	},
	{
		question: "Can Guerrilla Mail send email?",
		answer:
			"Yes — Guerrilla Mail is one of the only major disposable email services that lets you compose and send outbound email from your temporary address. ineedatest.email is inbound-only and cannot send. If you need to send from a throwaway address, Guerrilla Mail has a genuine edge here.",
	},
	{
		question: "How long does a Guerrilla Mail inbox last?",
		answer:
			"Guerrilla Mail deletes all messages after approximately one hour from delivery. The expiry is automatic and fixed — you cannot extend or shorten it. ineedatest.email lets you set your own expiry timer when you create the address, so you control exactly how long the inbox stays live.",
	},
	{
		question: "Are Guerrilla Mail addresses blocked by websites?",
		answer:
			"Guerrilla Mail's domains are on most major disposable-email blocklists. Amazon, many financial services, and a significant number of SaaS products reject guerrillamail.com addresses at the sign-up form. ineedatest.email uses multiple receiving domains, and less widely-known domains pass more blocklist checks — though no disposable email service is immune.",
	},
	{
		question: "What is Guerrilla Mail's Scrambled Address?",
		answer:
			"Scrambled Address is a Guerrilla Mail feature that obfuscates your real temporary address by generating a forwarding alias. It adds a layer of indirection so the displayed address in your browser session is not the underlying mailbox address. ineedatest.email takes a different approach: the inbox URL itself is the credential, and there is no public directory — nobody can browse to your inbox without the exact URL.",
	},
];

const comparisonRows: {
	feature: string;
	ours: { state: "yes" | "no" | "partial"; note?: string };
	theirs: { state: "yes" | "no" | "partial"; note?: string };
}[] = [
	{
		feature: "Completely free",
		ours: { state: "yes" },
		theirs: { state: "yes" },
	},
	{
		feature: "Account required",
		ours: { state: "no" },
		theirs: { state: "no" },
	},
	{
		feature: "User-controlled expiry",
		ours: { state: "yes", note: "Set your own timer" },
		theirs: { state: "no", note: "Fixed ~1 hr auto-delete" },
	},
	{
		feature: "Outbound sending",
		ours: { state: "no", note: "Inbound-only by design" },
		theirs: { state: "yes", note: "Compose and send from temp address" },
	},
	{
		feature: "Private inbox (no public directory)",
		ours: { state: "yes", note: "URL is the credential" },
		theirs: { state: "partial", note: "Scrambled Address option helps" },
	},
	{
		feature: "Multiple domains",
		ours: { state: "yes" },
		theirs: { state: "yes" },
	},
	{
		feature: "Attachment support",
		ours: { state: "no", note: "Text and HTML bodies only" },
		theirs: { state: "yes", note: "Receive and send attachments" },
	},
	{
		feature: "Open source",
		ours: { state: "no" },
		theirs: { state: "yes" },
	},
	{
		feature: "API access",
		ours: { state: "no" },
		theirs: { state: "no" },
	},
	{
		feature: "Blocked by major services",
		ours: { state: "partial", note: "Less common than Guerrilla Mail" },
		theirs: { state: "yes", note: "On most disposable-email blocklists" },
	},
	{
		feature: "Track record",
		ours: { state: "partial", note: "New service" },
		theirs: { state: "yes", note: "15+ years, well-established" },
	},
	{
		feature: "Mobile app",
		ours: { state: "no" },
		theirs: { state: "no" },
	},
];

const switchReasons = [
	{
		icon: IconClock,
		title: "You control how long the inbox lives",
		body: "Guerrilla Mail's one-hour auto-delete is fixed — there is no way to shorten it if you want the address gone in ten minutes, or extend it if you need it for a day. ineedatest.email lets you set the expiry when you create the address. The inbox is yours for exactly as long as you need and gone when you say.",
	},
	{
		icon: IconLock,
		title: "No public directory to stumble into",
		body: "Guerrilla Mail inboxes are technically private, but the site has had a public search in the past and many people assume @guerrillamail.com addresses are shared. ineedatest.email works differently: the inbox URL is the credential. There is no search, no listing, and no way to access your inbox without the exact URL you were given.",
	},
	{
		icon: IconShieldLock,
		title: "Fewer blocklist hits on common sign-up forms",
		body: "guerrillamail.com has been a well-known disposable domain for over fifteen years. It appears on virtually every major blocklist. Newer domains on ineedatest.email get through more sign-up forms and verification flows — not all of them, but enough to matter for the trials and one-off registrations where you want a real-feeling address.",
	},
	{
		icon: IconX,
		title: "Built to receive, not to do more",
		body: "Guerrilla Mail added outbound sending, scrambled addresses, and various features over the years. That scope means more surface area and more maintenance complexity. ineedatest.email does exactly one thing: receive email at an address that expires. If you only need to receive a code or confirmation, smaller scope is an advantage.",
	},
];

const honestyRows = [
	{
		feature: "Outbound sending from a throwaway address",
		body: "Guerrilla Mail lets you compose and send email directly from your temporary address — something ineedatest.email cannot do. If you need to reply to a vendor, send a test message, or initiate a conversation from a throwaway identity, Guerrilla Mail is the right tool. ineedatest.email is strictly inbound-only.",
	},
	{
		feature: "15+ years of established history",
		body: "Guerrilla Mail has been running since 2006 and is open source. That track record means predictable uptime, a large user community, and battle-tested infrastructure. ineedatest.email is a newer solo side project — it is not in the same tier of institutional trust, and anyone relying on it for anything important should factor that in.",
	},
	{
		feature: "Attachment send and receive",
		body: "Guerrilla Mail handles attachments in both directions — you can receive files and send them in outbound messages. ineedatest.email intentionally drops binary attachments and stores only text and HTML bodies. If the email you are expecting contains a PDF, image, or file, ineedatest.email will not have it.",
	},
];

function StateCell({
	state,
	note,
}: {
	state: "yes" | "no" | "partial";
	note?: string;
}) {
	return (
		<div className="flex flex-col gap-0.5">
			<span className="flex items-center gap-1.5">
				{state === "yes" && (
					<IconCheck className="size-4 shrink-0 text-green-600 dark:text-green-500" />
				)}
				{state === "no" && (
					<IconX className="size-4 shrink-0 text-destructive" />
				)}
				{state === "partial" && (
					<IconMinus className="size-4 shrink-0 text-amber-500" />
				)}
				<span className="text-xs font-medium text-foreground capitalize">
					{state === "partial" ? "limited" : state}
				</span>
			</span>
			{note && (
				<span className="text-xs text-muted-foreground leading-snug">
					{note}
				</span>
			)}
		</div>
	);
}

export const Route = createFileRoute("/_marketing/alternatives/guerrilla-mail")(
	{
		component: RouteComponent,
		head: () =>
			seo({
				title: "Guerrilla Mail alternative — ineedatest.email",
				description:
					"Free disposable inboxes with user-controlled expiry and no public directory. See how ineedatest.email compares to Guerrilla Mail.",
				keywords:
					"guerrilla mail alternative, guerrillamail alternatives, alternative to guerrilla mail, disposable email, temporary email",
				type: "FAQPage",
				faqItems: faqs,
			}),
	},
);

export function RouteComponent() {
	return (
		<MarketingShell>
			{/* Hero */}
			<section className="pb-12 pt-20 sm:pb-16 sm:pt-28">
				<MarketingContainer>
					<nav className="mb-6 text-xs text-muted-foreground">
						<a href="/" className="hover:text-foreground">
							ineedatest.email
						</a>
						<span className="mx-2">/</span>
						<span>Guerrilla Mail alternative</span>
					</nav>
					<div className={cn("max-w-3xl", MARKETING_SECTION_ANIM_BASE)}>
						<p className="mb-3 text-sm font-medium uppercase tracking-wider text-primary">
							Guerrilla Mail alternative
						</p>
						<h1 className="mb-4 text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
							Looking for a Guerrilla Mail alternative?
						</h1>
						<p className="text-lg text-pretty text-muted-foreground sm:text-xl">
							Guerrilla Mail's one-hour fixed expiry and aging domain reputation
							mean many sign-up forms reject it outright. ineedatest.email gives
							you private disposable inboxes with expiry you control — free, no
							account.
						</p>
					</div>
				</MarketingContainer>
			</section>

			{/* Comparison table */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="Comparison"
						heading="Best Guerrilla Mail alternatives in 2026"
						description="How ineedatest.email compares to Guerrilla Mail across the scenarios that matter: receiving OTPs, signing up for trials, and staying out of your real inbox."
						className="mb-10 sm:mb-12"
					/>
					<div className="overflow-x-auto rounded-xl border border-border/60">
						<table className="w-full min-w-[560px] text-sm">
							<thead>
								<tr className="border-b border-border/60 bg-muted/30">
									<th className="py-3 pl-5 pr-4 text-left font-semibold text-foreground">
										Feature
									</th>
									<th className="px-4 py-3 text-left font-semibold text-primary">
										ineedatest.email
									</th>
									<th className="px-4 py-3 text-left font-semibold text-foreground">
										Guerrilla Mail
									</th>
								</tr>
							</thead>
							<tbody>
								{comparisonRows.map((row, i) => (
									<tr
										key={row.feature}
										className={cn(
											"border-b border-border/40 last:border-0",
											i % 2 === 0 ? "bg-transparent" : "bg-muted/10",
										)}
									>
										<td className="py-3 pl-5 pr-4 font-medium text-foreground">
											{row.feature}
										</td>
										<td className="px-4 py-3">
											<StateCell {...row.ours} />
										</td>
										<td className="px-4 py-3">
											<StateCell {...row.theirs} />
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<p className="mt-3 text-xs text-muted-foreground">
						Guerrilla Mail features sourced from their public site. Accurate as
						of May 2026.
					</p>
				</MarketingContainer>
			</section>

			{/* Why switch */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="Why switch"
						heading="When ineedatest.email fits better than Guerrilla Mail"
						description="Guerrilla Mail has more features. ineedatest.email has fewer — deliberately. Here is when that trade-off is the right one."
						className="mb-10 sm:mb-12"
					/>
					<div className="grid gap-6 sm:gap-8 lg:grid-cols-2">
						{switchReasons.map((reason, index) => {
							const Icon = reason.icon;
							return (
								<div
									key={reason.title}
									className={cn(
										"flex gap-5 rounded-xl border border-border/80 bg-card/30 p-6 ring-1 ring-border/40 sm:p-8",
										MARKETING_SECTION_ANIM_BASE,
									)}
									style={{ animationDelay: `${index * 0.1}s` }}
								>
									<div className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted/40">
										<Icon className="size-5 text-primary" aria-hidden />
									</div>
									<div className="flex min-w-0 flex-col gap-2">
										<h3 className="text-base font-semibold tracking-tight text-foreground">
											{reason.title}
										</h3>
										<p className="text-sm text-pretty text-muted-foreground">
											{reason.body}
										</p>
									</div>
								</div>
							);
						})}
					</div>
				</MarketingContainer>
			</section>

			{/* Honesty section */}
			<section className="border-t border-border/50 bg-muted/15 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="Honest take"
						heading="Where Guerrilla Mail still wins"
						description="Three real situations where Guerrilla Mail is the right choice, not ineedatest.email."
						className="mb-10 sm:mb-12"
					/>
					<div className="grid gap-5 sm:gap-6 lg:grid-cols-3">
						{honestyRows.map((row) => (
							<div
								key={row.feature}
								className="rounded-xl border border-border/60 bg-card/30 p-6 ring-1 ring-border/30"
							>
								<h3 className="mb-2 text-sm font-semibold text-foreground">
									{row.feature}
								</h3>
								<p className="text-sm text-pretty text-muted-foreground">
									{row.body}
								</p>
							</div>
						))}
					</div>
				</MarketingContainer>
			</section>

			{/* FAQ */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="FAQ"
						heading="Questions about switching from Guerrilla Mail"
						className="mb-10 sm:mb-12"
					/>
					<div className="divide-y divide-border/50">
						{faqs.map((faq) => (
							<div key={faq.question} className="py-6 first:pt-0 last:pb-0">
								<h3 className="mb-2 text-base font-semibold text-foreground">
									{faq.question}
								</h3>
								<p className="text-sm text-pretty text-muted-foreground leading-relaxed">
									{faq.answer}
								</p>
							</div>
						))}
					</div>
				</MarketingContainer>
			</section>

			{/* CTA + sibling links */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
						<div>
							<p className="text-base font-semibold text-foreground">
								Try ineedatest.email — no account, no setup
							</p>
							<p className="mt-1 text-sm text-muted-foreground">
								Open the homepage, pick a domain, set a timer, copy the address.
							</p>
						</div>
						<a
							href="/"
							className="shrink-0 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
						>
							Get a disposable inbox
						</a>
					</div>

					<div className="mt-12 border-t border-border/50 pt-8">
						<p className="mb-3 text-sm text-muted-foreground">
							See what ineedatest.email can do:{" "}
							<a
								href="/features"
								className="text-primary underline-offset-4 hover:underline"
							>
								product features
							</a>
						</p>
						<p className="mb-4 text-sm font-medium text-muted-foreground">
							Compare other disposable email services
						</p>
						<div className="flex flex-wrap gap-3">
							<a
								href="/alternatives/mailinator"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								Mailinator alternatives
							</a>
							<a
								href="/alternatives/temp-mail"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								Temp Mail alternatives
							</a>
							<a
								href="/alternatives/10minutemail"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								10MinuteMail alternatives
							</a>
						</div>
					</div>
				</MarketingContainer>
			</section>
		</MarketingShell>
	);
}
